import React, { useEffect, useRef } from "react";
import { Button, ColorInput, Container, DEFAULT_THEME, Divider, Group, MultiSelect, Paper, ScrollArea, Select, Space, Stack, Text, TextInput, Textarea, Title, useMantineTheme } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import { IconCheck, IconChevronLeft, IconUser } from "@tabler/icons-react";

import { useGetTeamsQuery } from "../../api/team.api";
import { useGetUsersQuery } from "../../api/user.api";
import { MutableTeamDto, TeamDto } from "../../models/team.models";
import { useGetLocationsQuery } from "../../api/location.api";
import { RoleTemplate } from "../RoleTemplate";
import { AzureADRoles } from "../../Auth/auth-roles";
import classes from './TeamDetailView.module.css';

interface TeamEditViewProps {
	team?: TeamDto | null;
	onSave: (values: MutableTeamDto) => void;
	onClose: () => void;
	isSaving?: boolean;
	// isChanged?: boolean;
	onChanged?: (changed: boolean) => void;
}

export const TeamEditView: React.FC<TeamEditViewProps> = ({
	team,
	onSave,
	onClose,
	isSaving = false,
	// isChanged = false,
	onChanged,
}) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);

	const { data: teams } = useGetTeamsQuery();
	const teamItems = teams?.map(team => ({ value: team.TeamId, label: team.Name })) || [];

	const { data: users } = useGetUsersQuery();
	const userItems = users?.filter(user => user.Deleted == false).map(user => ({ value: user.UserId, label: user.Name })) || [];

	const { data: locations } = useGetLocationsQuery();
	const locationItems = locations?.map(location => ({ value: location.LocationId, label: location.Title })) || [];

	const isNew = !team || team.TeamId === "new";

	const form = useForm<MutableTeamDto>({
		initialValues: {
			TeamId: team?.TeamId || "",
			Name: team?.Name || "",
			Description: team?.Description || "",
			Colour: team?.Colour || "#1c7ed6",
			TeamLead: team?.TeamLead.UserId || "",
			Members: team?.Members.map(t => t.UserId) || [],
			Locations: team?.Locations.map(l => l.LocationId) || []
		},
		validate: {
			Name: (value) => (value.length < 2 ? "Name must be at least 2 characters long" : null),
			// Description: (value) => (value.length < 2 ? "Description must be at least 2 characters long" : null),
			Colour: (value) => (value.length < 2 ? "Colour must be at least 2 characters long" : null),
			TeamLead: (value) => (value.length === 0 ? "Team lead must be selected" : null),
			// Members: (value) => (value.length === 0 ? "At least one member must be selected" : null),
			// Locations: (value) => (value.length === 0 ? "At least one location must be selected" : null),
		},
	});

	// Track dirty state and call onChanged
	const prevDirty = useRef(form.isDirty());
	useEffect(() => {
		if (onChanged && prevDirty.current !== form.isDirty()) {
			onChanged(form.isDirty());
			prevDirty.current = form.isDirty();
		}
	}, [form.isDirty(), onChanged]);

	useEffect(() => {
		form.setValues({
			Name: team?.Name || "",
			Description: team?.Description || "",
			Colour: team?.Colour || "#1c7ed6",
			TeamLead: team?.TeamLead.UserId || "",
			Members: team?.Members.map(t => t.UserId) || [],
			Locations: team?.Locations.map(l => l.LocationId) || []
		});
		// eslint-disable-next-line
	}, [team]);

	const handleSave = (values: typeof form.values) => {
		if (!form.isValid()) {
			form.validate();
			return;
		}
		onSave(values);
	};

	const noAccess = (
		<Paper withBorder p="md" radius="md">
			<Text>You do not have permission to create or edit teams. Please contact your administrator.</Text>
		</Paper>
	);

	return <RoleTemplate role={AzureADRoles.Admin} noAccess={noAccess}>
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: "hidden" }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 160px)' }}>
				<Stack gap={0} mb="md" justify="center" className={classes['header']}>
					<Group justify="space-between" px="20" wrap="nowrap">
						<Group gap="xs" wrap="nowrap">
							<Button variant="subtle" radius="xl" ml={-15} onClick={onClose} miw={100} leftSection={<IconChevronLeft size={20} />}>
								Back
							</Button>
							<Title order={isMobile ? 6 : 3}>{isNew ? "New Team" : "Edit Team"}</Title>
						</Group>
						<Button
							onClick={() => handleSave(form.values)}
							disabled={!form.isDirty()}
							loading={isSaving}
							variant="filled"
							radius="xl"
							miw={100}
							leftSection={<IconCheck />}
						>
							Save
						</Button>
					</Group>
				</Stack>
				<Container px="20">
					<form onSubmit={form.onSubmit((values) => handleSave(values))}>
						<TextInput
							label="Name"
							key={form.key("Name")}
							{...form.getInputProps("Name")}
							placeholder="Enter team name"
							maxLength={255}
							required
							mb="xl"
						/>

						<TextInput
							label="Description"
							key={form.key("Description")}
							{...form.getInputProps("Description")}
							placeholder="Enter team description"
							maxLength={255}
							mb="xl"
						/>

						<ColorInput
							key={form.key("Colour")}
							{...form.getInputProps("Colour")}
							mb="xl"
							placeholder="Pick colour"
							label="Colour"
							disallowInput
							withPicker={false}
							swatchesPerRow={10}
							swatches={[
								...DEFAULT_THEME.colors.red,
								...DEFAULT_THEME.colors.pink,
								...DEFAULT_THEME.colors.grape,
								...DEFAULT_THEME.colors.violet,
								...DEFAULT_THEME.colors.indigo,
								...DEFAULT_THEME.colors.blue,
								...DEFAULT_THEME.colors.cyan,
								...DEFAULT_THEME.colors.green,
								...DEFAULT_THEME.colors.teal,
								...DEFAULT_THEME.colors.lime,
								...DEFAULT_THEME.colors.yellow,
								...DEFAULT_THEME.colors.orange
							]}
						/>

						<Select
							label="Team Lead"
							key={form.key("TeamLead")}
							{...form.getInputProps("TeamLead")}
							value={form.values.TeamLead}
							placeholder="Select team lead"
							data={userItems}
							required
							searchable
							leftSection={<IconUser size={16} />}
							mb="xl"
						/>

						<MultiSelect
							label="Members"
							key={form.key("Members")}
							{...form.getInputProps("Members")}
							value={form.values.Members}
							placeholder="Select members"
							data={userItems}
							searchable
							leftSection={<IconUser size={16} />}
							mb="xl"
						/>
						
						<MultiSelect
							label="Locations"
							key={form.key("Locations")}
							{...form.getInputProps("Locations")}
							value={form.values.Locations}
							placeholder="Select locations"
							data={locationItems}
							searchable
							leftSection={<IconUser size={16} />}
							mb="xl"
						/>
					</form>
				</Container>
			</ScrollArea>
		</Paper>
	</RoleTemplate>
};

export default TeamEditView;